import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Wifi, Terminal as TerminalIcon, ArrowDown } from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { cn } from '@/utils/cn';
import { can } from '@/utils/permissions';

// Gängige Konsolen-Befehle für Tab-Autocomplete (Minecraft-Fokus, tut bei
// anderen Spielen nicht weh — Tab schlägt nur vor, erzwingt nichts).
const COMMON_COMMANDS = [
  'help', 'list', 'say ', 'op ', 'deop ', 'kick ', 'ban ', 'ban-ip ', 'pardon ',
  'whitelist add ', 'whitelist remove ', 'whitelist on', 'whitelist off', 'whitelist list',
  'gamemode survival ', 'gamemode creative ', 'gamemode adventure ', 'gamemode spectator ',
  'gamerule ', 'give ', 'tp ', 'teleport ', 'time set day', 'time set night', 'time set noon',
  'weather clear', 'weather rain', 'weather thunder', 'difficulty peaceful', 'difficulty easy',
  'difficulty normal', 'difficulty hard', 'stop', 'save-all', 'seed', 'xp add ', 'effect give ',
  'enchant ', 'summon ', 'setworldspawn', 'spawnpoint ', 'kill ', 'clear ', 'locate structure ',
  'msg ', 'tell ', 'scoreboard ',
];

const HISTORY_LIMIT = 50;
const historyKey = (serverId: string) => `gm_console_history_${serverId}`;
const loadHistory = (serverId: string): string[] => {
  try { return JSON.parse(localStorage.getItem(historyKey(serverId)) || '[]'); } catch { return []; }
};

export const ServerConsole = observer(() => {
  const { activeServer, command, handleCommand } = serverStore;
  const { currentUser } = userStore;
  const logsEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = React.useState(true);
  const [showScrollButton, setShowScrollButton] = React.useState(false);

  // Befehls-Historie (Pfeiltasten) + Tab-Autocomplete
  const [history, setHistory] = React.useState<string[]>([]);
  const [histIndex, setHistIndex] = React.useState(-1); // -1 = am "frischen" Ende
  const draftRef = useRef('');
  const [suggestion, setSuggestion] = React.useState<string | null>(null);

  // Fetch full history on mount or server switch
  useEffect(() => {
    if (activeServer?.id) {
        serverStore.fetchServerLogHistory(activeServer.id);
        setHistory(loadHistory(activeServer.id));
        setHistIndex(-1);
        setSuggestion(null);
    }
  }, [activeServer?.id]);

  /** Bester Autocomplete-Kandidat für die aktuelle Eingabe. */
  const findSuggestion = (value: string): string | null => {
    const raw = value.replace(/^\//, '');
    if (!raw) return null;
    const players = activeServer ? (serverStore.serverRuntimeDetails.get(activeServer.id)?.playerCount?.players || []) : [];
    const words = raw.split(' ');
    if (words.length > 1) {
      // Ab dem zweiten Wort: Spielernamen vervollständigen
      const last = words[words.length - 1].toLowerCase();
      if (!last) return null;
      const hit = players.find((p) => p.toLowerCase().startsWith(last) && p.toLowerCase() !== last);
      if (!hit) return null;
      return (value.startsWith('/') ? '/' : '') + [...words.slice(0, -1), hit].join(' ');
    }
    const lower = raw.toLowerCase();
    const cmd = COMMON_COMMANDS.find((c) => c.startsWith(lower) && c.trim() !== lower);
    if (!cmd) return null;
    return (value.startsWith('/') ? '/' : '') + cmd;
  };

  const onCommandChange = (value: string) => {
    serverStore.command = value;
    setHistIndex(-1);
    setSuggestion(findSuggestion(value));
  };

  const pushHistory = (cmd: string) => {
    const trimmed = cmd.trim();
    if (!trimmed || !activeServer) return;
    const next = [...history.filter((h) => h !== trimmed), trimmed].slice(-HISTORY_LIMIT);
    setHistory(next);
    try { localStorage.setItem(historyKey(activeServer.id), JSON.stringify(next)); } catch { /* voll/aus */ }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (history.length === 0) return;
      if (histIndex === -1) draftRef.current = command;
      const next = histIndex === -1 ? history.length - 1 : Math.max(0, histIndex - 1);
      setHistIndex(next);
      serverStore.command = history[next];
      setSuggestion(null);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (histIndex === -1) return;
      const next = histIndex + 1;
      if (next >= history.length) { setHistIndex(-1); serverStore.command = draftRef.current; }
      else { setHistIndex(next); serverStore.command = history[next]; }
      return;
    }
    if (e.key === 'Tab' && suggestion) {
      e.preventDefault();
      serverStore.command = suggestion;
      setSuggestion(null);
      return;
    }
    if (e.key === 'Enter') {
      pushHistory(command);
      setHistIndex(-1);
      setSuggestion(null);
    }
    handleCommand(e);
  };

  // Smart auto-scroll: Only scroll if we were already at bottom
  useEffect(() => {
    if (isAtBottom && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeServer?.logs, isAtBottom]);

  const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      // Check if near bottom (< 200px) - Increased threshold for better responsiveness
      const atBottom = scrollHeight - scrollTop - clientHeight < 200;
      setIsAtBottom(atBottom);
      setShowScrollButton(!atBottom);
  };

  const scrollToBottom = () => {
      if (logsEndRef.current) {
          logsEndRef.current.scrollIntoView({ behavior: "smooth" });
          setIsAtBottom(true);
          setShowScrollButton(false);
      }
  };

  const canViewConsole = can(currentUser, 'can_view_console');
  const canModerate = can(currentUser, 'can_moderate_server');

  if (!canViewConsole && !canModerate) return null;

  return (
    <div className="h-full flex flex-col glass-panel rounded-xl overflow-hidden shadow-2xl relative animate-in fade-in zoom-in-95 duration-300">
       <div className="bg-white/5 px-4 py-3 border-b border-line flex justify-between items-center">
        <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center border border-line">
                <TerminalIcon size={16} className="text-fg-muted" />
            </div>
            <div>
                 <div className="text-xs text-fg-muted font-medium uppercase tracking-wider">Live Konsole</div>
                 <div className="text-sm font-bold text-white flex items-center">
                    <span className="text-blue-400 mr-2">{activeServer?.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-fg-muted font-mono border border-line">{activeServer?.id}</span>
                 </div>
            </div>
        </div>
        {activeServer?.status === 'online' ? (
          <div className="flex items-center space-x-2 bg-green-500/10 px-3 py-1.5 rounded-full border border-green-500/20">
              <div className="w-2 h-2 rounded-full bg-online gm-live-dot" />
              <span className="text-xs font-bold text-green-400 tracking-wide uppercase">Online</span>
          </div>
        ) : (
          <div className="flex items-center space-x-2 bg-white/[0.04] px-3 py-1.5 rounded-full border border-line">
              <div className="w-2 h-2 rounded-full bg-fg-muted/60" />
              <span className="text-xs font-bold text-fg-muted tracking-wide uppercase">Gestoppt</span>
          </div>
        )}
      </div>

      <div 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          "flex-1 p-4 font-mono overflow-y-auto space-y-1 custom-scrollbar bg-black/40 relative",
          // Schriftgroesse aus dem Profil (QoL-Einstellung)
          ({ xs: 'text-xs', sm: 'text-sm', base: 'text-base' } as Record<string, string>)[
            userStore.currentUser?.preferences?.console_font || 'sm'
          ] || 'text-sm'
        )}
      >
        {activeServer?.logs.map((log, index) => (
            <div key={index} className="text-fg break-words hover:bg-white/5 px-2 py-0.5 rounded transition-colors border-l-2 border-transparent hover:border-blue-500/50">
                {log}
            </div>
        ))}
        <div ref={logsEndRef} />
      </div>

      {/* Floating Go To Bottom Button - Outside scroll container for sticky positioning */}
      {showScrollButton && (
        <div className="absolute bottom-20 right-8 z-50">
            <button 
                onClick={scrollToBottom}
                className="bg-blue-600 hover:bg-blue-500 text-white p-3 rounded-full shadow-[0_0_15px_rgba(255, 106, 61,0.5)] transition-all animate-in fade-in zoom-in duration-200 border border-white/20 hover:scale-105 active:scale-95"
                title="Nach unten scrollen"
            >
                <ArrowDown size={24} />
            </button>
        </div>
      )}

      {canModerate && (
        <div className="p-3 bg-white/5 border-t border-line-strong font-mono relative group">
           <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 to-transparent pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity duration-500" />
          {suggestion && (
            <div className="absolute -top-7 left-6 text-xs font-mono text-fg-muted bg-ink/95 border border-line rounded-md px-2.5 py-1 flex items-center gap-2 animate-in fade-in duration-150 z-10">
              <span className="text-fg">{suggestion}</span>
              <span className="text-[9px] border border-line-strong rounded px-1 py-px bg-white/5 font-sans">TAB</span>
            </div>
          )}
          <div className="flex items-center bg-black/40 border border-line-strong rounded-xl px-4 py-3 focus-within:ring-2 focus-within:ring-blue-500/50 focus-within:border-blue-500/50 transition-all shadow-inner">
             <span className="text-blue-400 mr-3 font-bold animate-pulse">{'>'}</span>
             <input
                type="text"
                value={command}
                onChange={(e) => onCommandChange(e.target.value)}
                onKeyDown={onKeyDown}
                className="flex-1 bg-transparent border-none outline-none text-white placeholder-fg-muted/50 font-medium"
                autoFocus
                placeholder={`Befehl ausführen... (↑/↓ Historie, Tab vervollständigt)`}
                spellCheck={false}
             />
             <div className="hidden sm:flex text-[10px] text-fg-muted font-sans border border-line-strong px-2 py-1 rounded bg-white/5">
                ENTER
             </div>
          </div>
        </div>
      )}
    </div>
  );
});
